import React, { useEffect, useState, useCallback } from 'react';
import Checkbox from '@material-ui/core/Checkbox';
import { format, parseISO } from 'date-fns';
import AppButton from '../../../../components/AppButton/AppButton';
import {
    ContentWrapper,
    Footer,
    HorizontalLine,
    ModalContainer,
    ModalHeader,
    ModalOverlay,
    RemoveAll,
    Section,
    SectionTitle,
    Title,
} from './Style';
import { DatePicker } from '@mui/x-date-pickers';

interface FilterDateRange {
    from?: string;
    to?: string;
}

interface Filters {
    invitedAt?: FilterDateRange;
    reportGeneratedAt?: FilterDateRange;
}

type DateType = 'lastWeek' | 'last14Days' | 'lastMonth';
type FromToKey = 'from' | 'to';

const presets: DateType[] = ['lastWeek', 'last14Days', 'lastMonth'];

const getDateRangePreset = (type: DateType): FilterDateRange => {
    const today = new Date();
    const end = today.toISOString().split('T')[0];
    const startDate = new Date();

    switch (type) {
        case 'lastWeek':
            startDate.setDate(today.getDate() - 7);
            break;
        case 'last14Days':
            startDate.setDate(today.getDate() - 14);
            break;
        case 'lastMonth':
            startDate.setMonth(today.getMonth() - 1);
            break;
    }

    return {
        from: startDate.toISOString().split('T')[0],
        to: end,
    };
};

const datePickerSlotProps = {
    textField: {
        variant: 'outlined' as const,
        fullWidth: true,
        size: 'small' as const,
        sx: {
            fontSize: '14px',
            '& input::placeholder': {
                fontSize: '13px',
                color: '#999',
            },
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#00a5ac',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#00a5ac',
            },
        },
    },
    day: {
        sx: {
            ':not(.Mui-selected)': {
                backgroundColor: '#fff',
                borderRadius: '24px',
            },
            '&.MuiPickersDay-root.Mui-selected': {
                backgroundColor: '#00a5ac',
                color: '#fff',
                borderRadius: '24px',
            },
            ':hover': {
                backgroundColor: '#00a5ac',
                color: '#fff',
                borderRadius: '24px',
            },
        },
    },
};

interface FilterModalProps {
    open: boolean;
    filters: Filters;
    sort: string;
    onApply: (filters: Filters, sort: string) => void;
    onClose: () => void;
    onClear: () => void;
}

const FilterModal: React.FC<FilterModalProps> = ({
    open,
    filters,
    sort,
    onApply,
    onClose,
    onClear,
}) => {
    const [localFilters, setLocalFilters] = useState<Filters>({});
    const [localSort, setLocalSort] = useState<string>('_score:desc');
    const [selectedPreset, setSelectedPreset] = useState<{
        invitedAt?: DateType | 'custom';
        reportGeneratedAt?: DateType | 'custom';
    }>({});

    const [dateErrors, setDateErrors] = useState<Record<string, boolean>>({});

    const detectPreset = useCallback(
        (range?: FilterDateRange): DateType | 'custom' | undefined => {
            if (!range?.from && !range?.to) return undefined;

            for (const preset of presets) {
                const expected = getDateRangePreset(preset);
                if (range.from === expected.from && range.to === expected.to) {
                    return preset;
                }
            }

            return 'custom';
        },
        [],
    );

    useEffect(() => {
        if (open) {
            setLocalFilters(filters);
            setLocalSort(sort);

            setSelectedPreset({
                invitedAt: detectPreset(filters.invitedAt),
                reportGeneratedAt: detectPreset(filters.reportGeneratedAt),
            });

            setDateErrors({});
        }
    }, [open, filters, sort, detectPreset]);

    const handlePresetToggle = (
        type: keyof Filters,
        preset: DateType | 'custom',
    ) => {
        const current = selectedPreset[type];
        if (current === preset) {
            setSelectedPreset({ ...selectedPreset, [type]: undefined });
            setLocalFilters({ ...localFilters, [type]: undefined });
        } else {
            setSelectedPreset({ ...selectedPreset, [type]: preset });
            if (preset !== 'custom') {
                setLocalFilters({
                    ...localFilters,
                    [type]: getDateRangePreset(preset),
                });
            }
        }
    };

    const renderPresetDates = (type: keyof Filters) => (
        <>
            {presets.map((presetKey) => (
                <label key={presetKey} className="checkbox-label">
                    {presetKey === 'lastWeek'
                        ? 'Last week'
                        : presetKey === 'last14Days'
                        ? 'Last 14 days'
                        : 'Last month'}
                    <Checkbox
                        className="checkbox-input"
                        classes={{ checked: 'checkbox-input-checked' }}
                        checked={selectedPreset[type] === presetKey}
                        onChange={() => handlePresetToggle(type, presetKey)}
                    />
                </label>
            ))}
            <label className="checkbox-label">
                Custom
                <Checkbox
                    className="checkbox-input"
                    classes={{ checked: 'checkbox-input-checked' }}
                    checked={selectedPreset[type] === 'custom'}
                    onChange={() => handlePresetToggle(type, 'custom')}
                />
            </label>
        </>
    );

    const renderDateInputs = (type: keyof Filters) =>
        (['from', 'to'] as FromToKey[]).map((key) => {
            const value = localFilters[type]?.[key];
            const parsed = value ? parseISO(value) : null;
            const isDisabled = selectedPreset[type] !== 'custom';

            const errorKey = `${type}.${key}`;

            return (
                <DatePicker
                    key={`${type}-${key}`}
                    label={key.charAt(0).toUpperCase() + key.slice(1)}
                    format="dd/MM/yyyy"
                    value={parsed}
                    disabled={isDisabled}
                    onChange={(date) =>
                        setLocalFilters({
                            ...localFilters,
                            [type]: {
                                ...localFilters[type],
                                [key]: date
                                    ? format(date, 'yyyy-MM-dd')
                                    : undefined,
                            },
                        })
                    }
                    onError={(error) => {
                        setDateErrors((prev) => ({
                            ...prev,
                            [errorKey]: !!error,
                        }));
                    }}
                    slotProps={datePickerSlotProps}
                />
            );
        });

    const isApplyDisabled = Object.values(dateErrors).some(Boolean);

    const handleClear = () => {
        setSelectedPreset({});
        setLocalFilters({});
        setDateErrors({});
        onClear();
    };

    const handleApply = () => {
        onApply(localFilters, localSort);
    };

    return open ? (
        <ModalOverlay onClick={onClose}>
            <ModalContainer onClick={(e) => e.stopPropagation()}>
                <ModalHeader>
                    <Title>Filter</Title>
                    <RemoveAll onClick={handleClear}>
                        Remove all filters
                    </RemoveAll>
                </ModalHeader>
                <HorizontalLine />
                <ContentWrapper>
                    <Section>
                        <SectionTitle>Registration date</SectionTitle>
                        {renderPresetDates('invitedAt')}
                        {renderDateInputs('invitedAt')}
                    </Section>
                    <Section>
                        <SectionTitle>Report generation date</SectionTitle>
                        {renderPresetDates('reportGeneratedAt')}
                        {renderDateInputs('reportGeneratedAt')}
                    </Section>
                    <Section>
                        <SectionTitle>Sort</SectionTitle>
                        <select
                            value={localSort}
                            onChange={(e) => setLocalSort(e.target.value)}
                            className="select">
                            <option value="_score:asc">
                                Most relevant first
                            </option>
                            <option value="invited_at:desc">
                                Registered: Newest first
                            </option>
                            <option value="invited_at:asc">
                                Registered: Oldest first
                            </option>
                            <option value="report_generated_at:desc">
                                Report: Newest first
                            </option>
                            <option value="report_generated_at:asc">
                                Report: Oldest first
                            </option>
                        </select>
                    </Section>
                </ContentWrapper>
                <Footer>
                    <AppButton fontSize="14px" onClick={onClose} color="gray">
                        Cancel
                    </AppButton>
                    <AppButton
                        fontSize="14px"
                        isDisabled={isApplyDisabled}
                        onClick={handleApply}>
                        Apply filters
                    </AppButton>
                </Footer>
            </ModalContainer>
        </ModalOverlay>
    ) : null;
};

export default FilterModal;
